import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate, useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Badge, Button, Flex, Spin } from "antd"
import { routes } from "router"
import { Icon } from "ui/Icon"
import { useTheme } from "context/AntdThemeContext"
import { cleanExperiencesByGuide, fetchExperiencesByGuide, widgetCartItemsSelector } from "store/experiences"
import searchIllustration from "assets/imagesV2/search.svg"
import WidgetExperienceCard from "../WidgetExperienceCard"
import SearchBar from "./SearchBar"
import CartHoldTimer from "../CartHold/CartHoldTimer"
import { hasCartItem } from "../bookingDescription"
import { toListDateParam } from "../helpers"
import styles from "./WidgetListing.module.css"

const WidgetListing = () => {
  const { t } = useTranslation()
  const { uuid } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { switchTheme } = useTheme()
  const { byGuide } = useSelector((state) => state.experiences)
  const cartCount = useSelector(widgetCartItemsSelector).filter(hasCartItem).length
  const [search, setSearch] = useState({ date: null, guests: null })
  const [page, setPage] = useState(1)
  // Remounts the search bar so Clear All also empties its unsubmitted fields.
  const [searchBarKey, setSearchBarKey] = useState(0)

  const runSearch = (nextSearch) => {
    setSearch(nextSearch)
    setPage(1)
    dispatch(cleanExperiencesByGuide())
    dispatch(fetchExperiencesByGuide(uuid, toListDateParam(nextSearch.date), 1, nextSearch.guests)).catch(() => {})
  }

  useEffect(() => {
    if (!uuid) return
    dispatch(cleanExperiencesByGuide())
    dispatch(fetchExperiencesByGuide(uuid, "")).catch(() => {})
  }, [uuid, dispatch])

  useEffect(() => {
    if (byGuide?.adventureMode) switchTheme(byGuide.adventureMode)
  }, [byGuide?.adventureMode, switchTheme])

  // The API filters by date and guest count, so its pages and total are the results as they are.
  const experiences = useMemo(() => byGuide?.data || [], [byGuide?.data])

  const totalCount = byGuide?.metaData?.total_results ?? experiences.length
  const hasMorePages = (byGuide?.data?.length || 0) < (byGuide?.metaData?.total_results || 0)
  const isLoading = !!byGuide?.loading
  const isFiltered = !!(search.date || search.guests)
  const guide = { name: byGuide?.businessName, logo: byGuide?.widgetLogoUrl, rating: byGuide?.rating }

  const handleSeeMore = () => {
    const nextPage = page + 1
    setPage(nextPage)
    dispatch(fetchExperiencesByGuide(uuid, toListDateParam(search.date), nextPage, search.guests)).catch(() => {})
  }

  const handleClearAll = () => {
    setSearchBarKey((key) => key + 1)
    runSearch({ date: null, guests: null })
  }

  // The booking selection (the cart's item) survives browsing: the experience page itself starts a
  // new one when a different experience is opened.
  const handleOpenExperience = (experience) => navigate(routes.widgetExperience({ uuid, id: experience.uuid }))

  // The toolbar's pieces move per width (WidgetListing.module.css): search, timer and cart in one
  // row on desktop; the timer on its own row below 992px; on a phone the cart sits beside the
  // "Available Experiences" heading with the timer under them.
  return (
    <div>
      <header className={styles.header}>
        <h1 className={styles.title}>{t("booking_widget.listing.title")}</h1>
        <p className={styles.subtitle}>{t("booking_widget.listing.subtitle")}</p>
      </header>
      <div className={styles.toolbar}>
        <div className={styles.searchArea}>
          <SearchBar key={searchBarKey} guideUuid={uuid} date={search.date} guests={search.guests} isSearching={isLoading} onSearch={runSearch} />
        </div>
        <div className={styles.timerArea}>
          <CartHoldTimer />
        </div>
        <div className={styles.cartArea}>
          <Badge count={cartCount} classNames={{ indicator: styles.cartBadge }} title={t("booking_widget.cart.item_count", { count: cartCount })}>
            <Button
              shape="round"
              className={styles.cartButton}
              aria-label={cartCount ? `${t("booking_widget.listing.open_cart")} (${t("booking_widget.cart.item_count", { count: cartCount })})` : t("booking_widget.listing.open_cart")}
              onClick={() => navigate(routes.widgetCart({ uuid }))}
              icon={<Icon type="feather" name="ShoppingCart" width={24} height={24} />}
            />
          </Badge>
        </div>
        <h2 className={styles.availableHeading}>{t("booking_widget.listing.available", { count: totalCount })}</h2>
      </div>

      <Flex vertical gap={24} className={styles.results}>
        <Spin spinning={isLoading}>
          {experiences.length > 0 && (
            <div className={styles.grid}>
              {experiences.map((experience) => (
                <WidgetExperienceCard key={experience.uuid} experience={experience} guide={guide} onOpen={handleOpenExperience} />
              ))}
            </div>
          )}
          {experiences.length === 0 && !isLoading && (
            <Flex vertical align="center" justify="center" gap={16} className={styles.emptyState}>
              <img src={searchIllustration} alt="" width={140} height={140} />
              <span className={styles.emptyText}>{t("booking_widget.listing.no_matches")}</span>
              {isFiltered && (
                <Button type="primary" shape="round" onClick={handleClearAll}>
                  {t("booking_widget.listing.clear_all")}
                </Button>
              )}
            </Flex>
          )}
        </Spin>
        {hasMorePages && (
          <Flex justify="center">
            <Button shape="round" color="primary" variant="outlined" loading={isLoading} onClick={handleSeeMore}>
              {t("booking_widget.listing.see_more")}
            </Button>
          </Flex>
        )}
      </Flex>
    </div>
  )
}

export default WidgetListing
